import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  input,
  Output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatSelectModule } from '@angular/material/select'
import { PipesModule } from '@mmx/pipes'
import { Card } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { EstimateBalanceApiService } from '../../services/estimate-balance-api.service'

/** Sentinel form value emitted when the user picks the "+ Add new card" option. */
export const NEW_CARD_SENTINEL = 'new'

@Component({
  selector: 'mmx-saved-card-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressBarModule,
    MatSelectModule,
    PipesModule,
    TranslateModule,
  ],
  templateUrl: './saved-card-select.component.html',
  styleUrls: ['./saved-card-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SavedCardSelectComponent),
      multi: true,
    },
  ],
})
export class SavedCardSelectComponent implements ControlValueAccessor {
  readonly patientId = input<string | null>(null)
  readonly allowNewCard = input<boolean>(false)
  readonly required = input<boolean>(false)
  readonly label = input<string>('balance.payment.card')
  readonly requiredErrorKey = input<string>('balance.payment.card-required')
  readonly testId = input<string>('saved-card-select')

  /** Fires after every successful fetch — lets parents apply sibling-form side-effects (default the radio, pre-select). */
  @Output() readonly cardsLoaded = new EventEmitter<Card[]>()

  /** Fires once per failed fetch. Parents that drive sibling-form side-effects from cardsLoaded skip them on error. */
  @Output() readonly loadFailed = new EventEmitter<void>()

  readonly cards = signal<Card[]>([])
  readonly loaded = signal<boolean>(false)
  readonly loading = signal<boolean>(false)

  readonly NEW_CARD = NEW_CARD_SENTINEL

  // Internal control mirrors the CVA value so `<mat-select>` can drive selection
  // and surface required-state errors locally without leaking the parent's NgControl.
  readonly control = new FormControl<string>('', { nonNullable: true })

  readonly selectedCard = computed<Card | null>(() => {
    const id = this.controlValue()
    if (!id || id === NEW_CARD_SENTINEL) {
      return null
    }
    return this.cards().find((card) => card.id === id) ?? null
  })

  private readonly controlValue = signal<string>('')
  private readonly api = inject(EstimateBalanceApiService)
  private readonly destroyRef = inject(DestroyRef)

  private onChange: (value: string) => void = () => { /* noop until registered */ }
  private onTouched: () => void = () => { /* noop until registered */ }

  constructor() {
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const next = value ?? ''
        this.controlValue.set(next)
        this.onChange(next)
      })

    effect(() => {
      this.patientId()
      this.fetchSavedCards()
    })

    effect(() => {
      const validators = this.required() ? [Validators.required] : []
      this.control.setValidators(validators)
      this.control.updateValueAndValidity({ emitEvent: false })
    })
  }

  writeValue(value: string | null): void {
    const next = value ?? ''
    this.controlValue.set(next)
    this.control.setValue(next, { emitEvent: false })
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false })
    } else {
      this.control.enable({ emitEvent: false })
    }
  }

  /** Refetch from the parent — used after a new card is added so it appears in the dropdown. */
  refresh(): void {
    this.fetchSavedCards()
  }

  onBlur(): void {
    this.onTouched()
  }

  private fetchSavedCards(): void {
    const patientId = this.patientId()
    if (!patientId) {
      this.cards.set([])
      this.loaded.set(true)
      return
    }
    this.loading.set(true)
    this.api.listSavedCards(patientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cards) => {
          const list = cards ?? []
          this.cards.set(list)
          this.loaded.set(true)
          this.loading.set(false)
          this.cardsLoaded.emit(list)
        },
        error: () => {
          this.cards.set([])
          this.loaded.set(true)
          this.loading.set(false)
          this.loadFailed.emit()
        },
      })
  }
}
